import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck, Users as UsersIcon, Sparkles, HardHat } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import AdminLiveBell from "./AdminLiveBell";
import AssignedTasksBell from "./AssignedTasksBell";
import UserLiveBell from "./UserLiveBell";
import ChatNavButton from "./ChatNavButton";
import LanguageSwitcher from "./LanguageSwitcher";

const publicLinks = [
  { to: "/outlet", key: "outlet" },
  { to: "/chi-siamo", key: "about" },
];

const privateLinks = [
  { to: "/outlet", key: "outlet" },
];

const realEstateLinks = [
  { to: "/outlet", key: "outlet" },
];

// Etichetta + colore del badge "area riservata" per ogni tipologia di account.
// Le label restano in italiano: sono identificative dell'area, non testo UI.
// Primo nome "utile" per il badge: scarta parole uguali al ruolo (es. name="Admin")
// e fallback sulla parte locale dell'email (gianluca@... → "Gianluca").
function badgeFirstName(user, roleLabel) {
  const skip = new Set([...roleLabel.toLowerCase().split(/\s+/), "archi-hub", "archihub"]);
  const words = (user.name || "").trim().split(/\s+/).filter(Boolean);
  const meaningful = words.find((w) => !skip.has(w.toLowerCase()));
  if (meaningful) return meaningful;
  const local = (user.email || "").split("@")[0].split(/[._-]/)[0];
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : "";
}

function getRoleBadge(user) {
  if (!user) return null;
  if (user.role === "admin") {
    return { role: "admin", label: `Admin ${badgeFirstName(user, "Admin")}`.trim(), cls: "bg-ink text-cream border-ink" };
  }
  if (user.account_type === "trader") {
    return { role: "trader", label: `Trader ${badgeFirstName(user, "Trader")}`.trim(), cls: "bg-amber-100 text-amber-900 border-amber-300" };
  }
  if (user.account_type === "real_estate") {
    return { role: "real-estate", label: `Real Estate ${badgeFirstName(user, "Real Estate")}`.trim(), cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
  }
  return { role: "studio", label: `Studio ${badgeFirstName(user, "Studio")}`.trim(), cls: "bg-slate-100 text-slate-700 border-slate-300" };
}

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const onHome = loc.pathname === "/";
  const isRealEstate = user && user.account_type === "real_estate";
  const links = user
    ? (isRealEstate ? realEstateLinks : privateLinks)
    : publicLinks;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Badge "novità fornitori" sulla voce ASSET: numero di fornitori con file nuovi/aggiornati
  const [newsCount, setNewsCount] = useState(0);
  useEffect(() => {
    if (user && user.role !== "admin" && user.account_type !== "real_estate") {
      api.get("/suppliers/news-count").then((r) => setNewsCount(r.data?.count || 0)).catch(() => {});
    } else {
      setNewsCount(0);
    }
  }, [user, loc.pathname]);

  // Badge "operai al lavoro": numero di installatori con entrata aperta (si aggiorna appena timbrano)
  const [atWork, setAtWork] = useState(0);
  useEffect(() => {
    if (!(user && user.role === "admin")) { setAtWork(0); return undefined; }
    let alive = true;
    const load = () => api.get("/installers/admin/at-work-count").then((r) => { if (alive) setAtWork(r.data?.count || 0); }).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [user, loc.pathname]);

  const glass = true;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${glass ? "glass-nav" : "bg-transparent"}`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1760px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="font-display font-extrabold tracking-[0.18em] text-xl md:text-2xl text-ink whitespace-nowrap" data-testid="nav-logo">
            ARCHI<span className="text-gold">—</span>HUB
          </Link>
          {(() => {
            const b = getRoleBadge(user);
            if (!b) return null;
            return (
              <span
                className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.18em] uppercase border whitespace-nowrap ${b.cls}`}
                data-testid="nav-role-badge"
                data-role={b.role}
                title={`${t("nav.reserved_area")} ${b.label}`}
              >
                {b.label}
              </span>
            );
          })()}
        </div>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.15em] uppercase transition-colors ${isActive ? "text-ink" : "text-slate1 hover:text-ink"}`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
          {user && user.role !== "admin" && (user.account_type === "trader" || user.account_type === "studio") && (
            <NavLink
              to="/magic-search"
              data-testid="nav-magic-search"
              className={({ isActive }) => `text-[13px] tracking-[0.15em] uppercase transition-colors inline-flex items-center gap-2 ${isActive ? "text-gold" : "text-slate1 hover:text-gold"}`}
              title="Ricerca intelligente nei listini"
            >
              <Sparkles size={14} /> Magic Search
            </NavLink>
          )}
          {user && user.role === "admin" ? (
            <>
              <NavLink to="/admin" className={({ isActive }) => `text-[13px] tracking-[0.15em] uppercase transition-colors inline-flex items-center gap-2 ${isActive ? "text-ink" : "text-slate1 hover:text-ink"}`} data-testid="nav-admin">
                <ShieldCheck size={14} /> {t("nav.admin")}
              </NavLink>
              <NavLink to="/preventivatore" className={({ isActive }) => `text-[13px] tracking-[0.15em] uppercase transition-colors inline-flex items-center gap-2 ${isActive ? "text-gold" : "text-slate1 hover:text-gold"}`} data-testid="nav-preventivatore" title="Super Magic Search (AI)">
                <Sparkles size={14} /> Preventivatore
              </NavLink>
              <Link to="/admin?tab=installers" className={`relative text-[13px] tracking-[0.15em] uppercase transition-colors inline-flex items-center gap-2 ${loc.search.includes("tab=installers") ? "text-ink" : "text-slate1 hover:text-ink"}`} data-testid="nav-installers" title="Installers · operai, timbrature e consegne">
                <HardHat size={14} /> Installers
                {atWork > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full" data-testid="nav-installers-atwork" title={`${atWork} operai al lavoro ora`}>{atWork}</span>
                )}
              </Link>
              {/* Admin: pulsante Chat a destra di Installers */}
              <ChatNavButton />
            </>
          ) : null}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              <AdminLiveBell user={user} />
              <AssignedTasksBell user={user} />
              <UserLiveBell user={user} />
              {user.role !== "admin" && !isRealEstate && (
                <>
                  <Link to="/progetti" className="text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-projects">
                    {t("nav.projects")}
                  </Link>
                  <Link to="/fornitori" className="relative text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-suppliers">
                    {t("nav.suppliers")}
                    {newsCount > 0 && (
                      <span
                        className="absolute -top-2 -right-4 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-gold text-cream text-[9px] font-semibold leading-none"
                        data-testid="nav-suppliers-news-badge"
                        title={t("suppliers.news_dot")}
                      >
                        {newsCount}
                      </span>
                    )}
                  </Link>
                  <Link to="/dashboard" className="inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-dashboard">
                    <LayoutDashboard size={14} /> {t("nav.dashboard")}
                  </Link>
                </>
              )}
              {user.role !== "admin" && isRealEstate && (
                <>
                  <Link to="/contatti" className="inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-contacts">
                    <UsersIcon size={14} /> {t("nav.contacts")}
                  </Link>
                  <Link to="/dashboard" className="inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-dashboard">
                    <LayoutDashboard size={14} /> {t("nav.dashboard")}
                  </Link>
                </>
              )}
              <LanguageSwitcher />
              {user.role !== "admin" && (
                <Link
                  to="/account"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors"
                  data-testid="nav-account"
                  title="Area riservata · cambia password"
                >
                  <UsersIcon size={13} /> Account
                </Link>
              )}
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="inline-flex items-center gap-2 px-4 py-2 border border-ink/20 text-[12px] tracking-[0.15em] uppercase text-ink hover:bg-ink hover:text-cream transition-colors rounded-sm"
                data-testid="nav-logout"
              >
                <LogOut size={14} /> {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              <Link to="/login" className="text-[13px] tracking-[0.15em] uppercase text-slate1 hover:text-ink transition-colors" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/register" className="px-5 py-2.5 bg-ink text-cream text-[12px] tracking-[0.15em] uppercase rounded-sm hover-lift" data-testid="nav-register">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-ink/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase text-slate1">{t(`nav.${l.key}`)}</Link>
            ))}
            {user && user.role !== "admin" && (user.account_type === "trader" || user.account_type === "studio") && (
              <Link to="/magic-search" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 text-gold" data-testid="nav-magic-search-mobile">
                <Sparkles size={14} /> Magic Search
              </Link>
            )}
            {user ? (
              <>
                {user.role === "admin" && <ChatNavButton mobile onOpened={() => setOpen(false)} />}
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.dashboard")}</Link>
                {user.role !== "admin" && !isRealEstate && <Link to="/progetti" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.projects")}</Link>}
                {user.role !== "admin" && !isRealEstate && (
                  <Link to="/fornitori" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2">
                    {t("nav.suppliers")}
                    {newsCount > 0 && (
                      <span className="min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-gold text-cream text-[9px] font-semibold leading-none" data-testid="nav-suppliers-news-badge-mobile">
                        {newsCount}
                      </span>
                    )}
                  </Link>
                )}
                {user.role !== "admin" && isRealEstate && <Link to="/contatti" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.contacts")}</Link>}
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.admin")}</Link>}
                {user.role === "admin" && (
                  <Link to="/preventivatore" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 text-gold" data-testid="nav-preventivatore-mobile">
                    <Sparkles size={14} /> Preventivatore
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link to="/admin?tab=installers" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 text-ink" data-testid="nav-installers-mobile">
                    <HardHat size={14} /> Installers
                    {atWork > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full">{atWork}</span>}
                  </Link>
                )}
                <div className="pt-1"><LanguageSwitcher /></div>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-sm tracking-[0.15em] uppercase">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase">{t("nav.register")}</Link>
                <div className="pt-1"><LanguageSwitcher /></div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
